import { CELL_SIZE, CRASH_ANIM_TICKS, FIREBALL_ORBIT_RADIUS, FIREBALL_ORBIT_STEP, FIREBALL_COUNT, UI_COLOR, YOU_INDICATOR_COMPRESS_MS } from '@shared/constants.js';
import { ScreenState } from '@shared/types.js';
import type { Crash, Player } from '@shared/types.js';
import { game } from '@client/state.js';
import { youIndicatorAlpha } from '@client/indicator.js';
import { drawSkull, drawFire, drawTaunt, drawLightcycle } from '@client/icons.js';
import { UnlockId, isUnlockActive } from '@client/unlocks.js';

const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;

// Reusable player lookup — avoids Map allocation per frame
const playerLookup: (Player | undefined)[] = [];

/** Pixel position of a player interpolated between the previous and current tick. */
function interpolatedPos(p: Player, t: number, cs: number): { px: number; py: number } {
  const prev = game.prevPositions[p.id];
  if (prev && t < 1) {
    return { px: (prev.x + (p.x - prev.x) * t) * cs, py: (prev.y + (p.y - prev.y) * t) * cs };
  }
  return { px: p.x * cs, py: p.y * cs };
}

function getWrapSize(): { w: number; h: number } {
  canvas.style.display = 'none'; /* cls-ok: temporary hide for measurement */
  const wrap = document.getElementById('canvas-wrap')!;
  const w = wrap.clientWidth - 4;
  const h = wrap.clientHeight - 4;
  canvas.style.display = ''; /* cls-ok: restore after measurement */
  return { w, h };
}

export function sizeCanvas(): void {
  const { w, h } = getWrapSize();
  game.cellSize = CELL_SIZE;
  game.cols = Math.max(10, Math.floor(w / CELL_SIZE));
  game.rows = Math.max(10, Math.floor(h / CELL_SIZE));
  game.width = game.cols * CELL_SIZE;
  game.height = game.rows * CELL_SIZE;
  canvas.width = game.width;
  canvas.height = game.height;
}

export function setCanvasSize(cols: number, rows: number): void {
  const { w, h } = getWrapSize();
  const cellSize = Math.max(4, Math.floor(Math.min(w / cols, h / rows)));
  game.cellSize = cellSize;
  game.cols = cols;
  game.rows = rows;
  game.width = cols * cellSize;
  game.height = rows * cellSize;
  canvas.width = game.width;
  canvas.height = game.height;
}

export function draw(): void {
  const { cols, rows, width, height, grid, players, crashes, crashTick, cellSize: cs } = game;

  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, width, height);

  // Grid lines — single batched path
  ctx.beginPath();
  ctx.strokeStyle = '#111';
  ctx.lineWidth = 0.5;
  for (let x = 0; x <= cols; x++) {
    ctx.moveTo(x * cs, 0); ctx.lineTo(x * cs, height);
  }
  for (let y = 0; y <= rows; y++) {
    ctx.moveTo(0, y * cs); ctx.lineTo(width, y * cs);
  }
  ctx.stroke();

  // Build reusable player lookup by ID
  playerLookup.length = 0;
  for (const p of players) playerLookup[p.id] = p;

  // Trails
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const cell = grid[y][x];
      if (cell !== 0) {
        const p = playerLookup[cell];
        if (p) {
          if (p.alive && x === p.x && y === p.y) continue;
          ctx.fillStyle = p.trailColor;
          ctx.fillRect(x * cs, y * cs, cs, cs);
        }
      }
    }
  }

  // Player heads — interpolated between ticks for smooth movement
  const t = game.lastTickTime > 0
    ? Math.min(1, (performance.now() - game.lastTickTime) / game.tickMs)
    : 1;

  for (const p of players) {
    if (!p.alive) continue;
    const { px, py } = interpolatedPos(p, t, cs);
    // Airborne jump: white pulse instead of normal head
    const isAirborne = game.jumpingTicks > 0 && game.jumpingPlayerId === p.id;
    if (isAirborne) {
      const pulse = 0.5 + 0.5 * Math.abs(Math.sin(performance.now() / 100));
      ctx.globalAlpha = pulse;
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(px + cs / 2, py + cs / 2, cs * 0.7, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    } else {
      const hasLightcycle = !p.isBot && isUnlockActive(UnlockId.LIGHTCYCLE_ICON, game.arenaUnlock);
      if (hasLightcycle) {
        drawLightcycle(ctx, px + cs / 2, py + cs / 2, cs * 1.6, p.color, 1.0, p.dir);
      } else {
        ctx.fillStyle = '#fff';
        ctx.fillRect(px, py, cs, cs);
        ctx.fillStyle = p.color;
        ctx.fillRect(px + 1, py + 1, cs - 2, cs - 2);
      }
    }
  }

  // Fire trail effect (unlock or debug)
  const fireActive = isUnlockActive(UnlockId.FIRE_TRAIL, game.arenaUnlock);
  if (fireActive) {
    for (const p of players) {
      if (!p.alive || p.isBot) continue;
      const trail = game.fireTrail[p.id];
      if (!trail) continue;
      for (let i = 0; i < trail.length; i++) {
        const pos = trail[i];
        const alpha = i === 0 ? 0.5 : 0.8;
        drawFire(ctx, (pos.x + 0.5) * cs, (pos.y + 0.5) * cs, cs * 1.2, alpha);
      }
    }
  }

  // Fireball aura effect — fireballs orbiting the player
  const auraActive = isUnlockActive(UnlockId.FIREBALL_AURA, game.arenaUnlock);
  if (auraActive && game.state === ScreenState.PLAYING) {
    for (const p of players) {
      if (!p.alive || p.isBot) continue;
      const pcx = (p.x + 0.5) * cs;
      const pcy = (p.y + 0.5) * cs;
      const orbitRadius = cs * FIREBALL_ORBIT_RADIUS;
      for (let i = 0; i < FIREBALL_COUNT; i++) {
        const baseAngle = (Math.PI * 2 * i) / FIREBALL_COUNT;
        const angle = baseAngle + game.crashTick * FIREBALL_ORBIT_STEP;
        const fx = pcx + Math.cos(angle) * orbitRadius;
        const fy = pcy + Math.sin(angle) * orbitRadius;
        drawFire(ctx, fx, fy, cs * 0.7, 0.7);
      }
    }
  }

  // "You are here" indicator — big white square zeroing in on the local piece,
  // exactly YOU_INDICATOR_CYCLES times from countdown start, then gone (#37)
  const indicatorElapsed = performance.now() - game.youIndicatorStart;
  const indicatorAlpha = youIndicatorAlpha(game.state, indicatorElapsed);
  if (indicatorAlpha > 0) {
    const myId = game.online ? game.onlinePlayerId : (game.players.find(p => !p.isBot)?.id ?? null);
    const myPlayer = myId !== null ? playerLookup[myId] : undefined;
    if (myPlayer && myPlayer.alive) {
      // Center on the same interpolated position as the head so the square always contains it
      const { px: hx, py: hy } = interpolatedPos(myPlayer, t, cs);
      // Compress from ~2.2 cells out down to just outside the piece, brightening as it locks on
      const phase = (indicatorElapsed % YOU_INDICATOR_COMPRESS_MS) / YOU_INDICATOR_COMPRESS_MS;
      const pad = cs * (2.2 - 2.0 * phase);
      ctx.save();
      ctx.globalAlpha = (0.35 + 0.65 * phase) * indicatorAlpha;
      ctx.strokeStyle = UI_COLOR.NEUTRAL;
      ctx.lineWidth = 4;
      ctx.shadowColor = myPlayer.color;
      ctx.shadowBlur = 14;
      ctx.strokeRect(hx - pad, hy - pad, cs + pad * 2, cs + pad * 2);
      ctx.restore();
    }
  }

  // Crashes — skip the winner's position (winner gets taunt, not skull)
  const winner = game.winnerId !== null ? playerLookup[game.winnerId] : undefined;
  for (const c of crashes) {
    if (winner && c.x === winner.x && c.y === winner.y) continue;
    drawCrash(c, crashTick, cs);
  }

  // Winner marker — taunting devil, slightly larger than cell
  if (game.winnerId !== null) {
    const winnerP = playerLookup[game.winnerId];
    if (winnerP) {
      const wx = winnerP.x * cs;
      const wy = winnerP.y * cs;
      drawTaunt(ctx, wx + cs / 2, wy + cs / 2, cs * 1.2, winnerP.color, 0.9);
    }
  }
}

// Lehmer LCG pseudo-random (deterministic from seed for repeatable particle patterns)
const LCG_MODULUS = 2147483647;
const LCG_MULTIPLIER = 16807;

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * LCG_MULTIPLIER) % LCG_MODULUS;
    return (s - 1) / (LCG_MODULUS - 1);
  };
}

const PARTICLE_COUNT = 14;
const SPARK_COUNT = 8;

function drawCrash(c: Crash, crashTick: number, cs: number): void {
  // Smooth fractional age using real-time interpolation
  const tickAge = crashTick - c.tick;
  const subTick = game.lastTickTime > 0
    ? Math.min(1, (performance.now() - game.lastTickTime) / game.tickMs)
    : 1;
  const age = tickAge + subTick;

  const cx = (c.x + 0.5) * cs;
  const cy = (c.y + 0.5) * cs;

  if (age < CRASH_ANIM_TICKS) {
    const progress = age / CRASH_ANIM_TICKS;
    const rand = seededRandom(c.x * 1000 + c.y * 37 + c.tick);

    // Phase 1: Bright white flash (first 20% of animation)
    if (progress < 0.2) {
      const flashAlpha = 1 - progress / 0.2;
      const flashRadius = cs * (0.8 + progress * 4);
      ctx.beginPath();
      ctx.arc(cx, cy, flashRadius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${flashAlpha * 0.6})`;
      ctx.fill();
    }

    // Phase 2: Inner core glow
    const coreAlpha = Math.max(0, 1 - progress * 1.5);
    if (coreAlpha > 0) {
      const coreRadius = cs * (0.3 + progress * 0.5);
      ctx.beginPath();
      ctx.arc(cx, cy, coreRadius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${coreAlpha})`;
      ctx.fill();
    }

    // Phase 3: Shockwave ring — expands fast, fades
    const waveProgress = Math.min(1, progress * 2);
    const waveAlpha = Math.max(0, 1 - waveProgress);
    if (waveAlpha > 0) {
      const waveRadius = cs * (0.5 + waveProgress * 4);
      ctx.beginPath();
      ctx.arc(cx, cy, waveRadius, 0, Math.PI * 2);
      ctx.strokeStyle = c.color;
      ctx.lineWidth = Math.max(1, cs * 0.15 * (1 - waveProgress));
      ctx.globalAlpha = waveAlpha * 0.7;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // Phase 4: Scattered debris particles
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const baseAngle = (Math.PI * 2 * i) / PARTICLE_COUNT;
      const angleOffset = (rand() - 0.5) * 0.8;
      const angle = baseAngle + angleOffset;
      const speed = 0.6 + rand() * 0.8;
      const decel = progress * progress;
      const dist = cs * speed * progress * (3 + rand() * 2) * (1 - decel * 0.4);
      const px = cx + Math.cos(angle) * dist;
      const py = cy + Math.sin(angle) * dist;
      const sizeBase = cs * (0.15 + rand() * 0.2);
      const size = sizeBase * (1 - progress * 0.8);
      const alpha = Math.max(0, 1 - progress * 1.2);

      ctx.globalAlpha = alpha;
      ctx.fillStyle = i % 3 === 0 ? '#fff' : c.color;
      ctx.fillRect(px - size / 2, py - size / 2, size, size);
    }
    ctx.globalAlpha = 1;

    // Phase 5: Tiny sparks — faster, smaller, linger longer
    for (let i = 0; i < SPARK_COUNT; i++) {
      const angle = rand() * Math.PI * 2;
      const speed = 1 + rand() * 1.5;
      const dist = cs * speed * progress * 4;
      const sx = cx + Math.cos(angle) * dist;
      const sy = cy + Math.sin(angle) * dist;
      const sparkSize = cs * 0.08 * (1 - progress);
      const sparkAlpha = Math.max(0, 1 - progress * 1.5);

      if (sparkAlpha > 0 && sparkSize > 0.5) {
        ctx.globalAlpha = sparkAlpha;
        ctx.fillStyle = '#fff';
        ctx.fillRect(sx - sparkSize / 2, sy - sparkSize / 2, sparkSize, sparkSize);
      }
    }
    ctx.globalAlpha = 1;
  }

  // Persistent skull marker
  drawSkull(ctx, cx, cy, cs * 1.2, c.color, 0.7);
}
